// @givmo/board — one meeting's Overview: where it is, its notice arithmetic, who's coming, its agenda.
//
// The title block, the actions and the tabs (Overview · Packet · Meeting record · Minutes) are the
// shared `MeetingFrame` — each tab is its page's own address, so a director's emailed packet link still
// opens the packet (CBG-UX1, Jonathan's approved mockups m2/m3).
//
// The notice is ONE line of arithmetic against the rule the organization recorded (`noticeFact`), and the
// backend's full sentence sits behind the info glyph beside it, VERBATIM — the sentence states its own
// counting method and zone and concludes nothing, which is why it is never paraphrased or composed
// here; it already states a shortfall in days, so nothing repeats it beside the sentence. The
// calendar file is fetched on demand (the frame's "Add to calendar"), because the backend regenerates
// it from the stored row each time — which is what carries a rescheduled meeting's higher sequence
// number and makes a calendar application update its existing entry instead of adding a second one.
//
// CBG-CAL1 slice 1d adds three things, each dark until served (`features/calendar/`): the meeting's
// Invitations line in this card (the meeting read's `calendar` block, with its one act), where the
// last reschedule was made when a calendar made it ("1 time, in Google Calendar"), and the meeting's
// History at the foot, when the calendar is in play for it.

import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgBoardMeetingOut } from '@givmo/api-client';
import { Button, Card, CardBody, Skeleton } from '@givmo/design-system';
import { useParams } from '../../board-routing';
import { PageShell } from '../../components/page-shell';
import { InfoTip } from '../../components/fine-print';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { TermSuggestionCard } from './term-suggestion-card';
import { CommitteeSuggestionCard } from './committee-suggestion-card';
import { useBoardOperations } from '../../board-runtime';
import { useMeetingEvents } from '../../calendar/calendar-queries';
import { calendarWords } from '../../calendar/calendar-words';
import { MeetingHistoryCard, lastRescheduleProvider } from '../calendar/history-card';
import {
  MeetingInvitationsLine,
  useMeetingCalendarView,
  type MeetingCalendarView,
} from '../calendar/meeting-calendar';
import { AgendaPanel } from './agenda-panel';
import { AttendanceIntentPanel } from './attendance-intent-panel';
import { MeetingFrame } from './meeting-frame';
import { meetingCommitteeName, noticeFact } from './meetings-decode';

export function MeetingDetailPage() {
  const operations = useBoardOperations();
  const { meetingId } = useParams({ from: '/authenticated/meetings/$meetingId' });
  const { orgRef, isReady } = useBoardHost();

  const meetingQuery = useQuery({
    ...operations.officerGetBoardMeetingOptions({
      path: { org_ref: orgRef ?? '', meeting_id: meetingId },
    }),
    enabled: isReady && orgRef !== null,
  });
  const meeting = meetingQuery.data ?? null;
  const queryClient = useQueryClient();
  const refreshMeeting = () =>
    void queryClient.invalidateQueries({
      queryKey: operations.officerGetBoardMeetingOptions({
        path: { org_ref: orgRef ?? '', meeting_id: meetingId },
      }).queryKey,
    });

  if (meetingQuery.isPending && isReady) {
    return (
      <PageShell title="Meeting" testId="page-meeting-detail">
        <Skeleton className="h-90 w-full rounded-lg" testId="meeting-detail-loading" />
      </PageShell>
    );
  }

  if (meetingQuery.isError || meeting === null) {
    return (
      <PageShell title="Meeting" testId="page-meeting-detail">
        <Card testId="meeting-detail-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this meeting.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void meetingQuery.refetch()}
              testId="meeting-detail-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  return (
    <MeetingFrame meeting={meeting} tab="overview" testId="page-meeting-detail">
      <MeetingOverview meeting={meeting} onCancelled={refreshMeeting} />
    </MeetingFrame>
  );
}

/** The Overview's body, beneath the frame (its calendar reads hang off the loaded meeting). */
function MeetingOverview({
  meeting,
  onCancelled,
}: {
  meeting: OrgBoardMeetingOut;
  onCancelled: () => void;
}) {
  const { orgRef } = useBoardHost();
  const canWriteAgenda = useBoardPermission('board_meeting:write');
  const calendar = useMeetingCalendarView(meeting);
  const isCancelled = meeting.status === 'cancelled';
  return (
    <>
      <MeetingDetails meeting={meeting} calendar={calendar} onCancelled={onCancelled} />

      {/* CBG-YR6-CONFIRM (frame c7): on the board's own meeting, for someone who can change its
          agenda, the decisions of each committee whose practice is to suggest them for the board's
          next meeting — a suggestion that adds nothing until pressed (backend §4–§6). The read says
          whether it is offered: never on a committee's meeting or once the minutes are approved. */}
      {orgRef !== null && !isCancelled && !meeting.committee_id && canWriteAgenda ? (
        <CommitteeSuggestionCard orgRef={orgRef} meetingId={meeting.id} />
      ) : null}

      {/* Above the agenda: an officer opening a meeting before it happens is chasing answers, and
          the agenda is what they build once they know who will be there. A cancelled meeting is
          nobody's to attend, so it has no "Who's coming" at all. */}
      {orgRef !== null && !isCancelled ? (
        <AttendanceIntentPanel
          orgRef={orgRef}
          meetingId={meeting.id}
          cancelled={isCancelled}
          startsAt={meeting.starts_at ?? null}
          calendar={calendar.block}
          committeeId={meeting.committee_id ?? null}
        />
      ) : null}

      {/* CBG-YR3-ANNUAL (frame t4): on an annual meeting, for someone who can change its agenda, the
          terms ending by it — a suggestion that adds nothing until pressed (backend §8). */}
      {orgRef !== null && !isCancelled && meeting.meeting_type === 'annual' && canWriteAgenda ? (
        <TermSuggestionCard orgRef={orgRef} meetingId={meeting.id} />
      ) : null}

      {orgRef !== null ? (
        <AgendaPanel orgRef={orgRef} meetingId={meeting.id} cancelled={isCancelled} />
      ) : null}

      {/* The meeting's own history, newest first, when the calendar is in play for it. */}
      <MeetingHistoryCard
        meetingId={meeting.id}
        calendar={calendar.block}
        timeZone={meeting.time_zone}
      />
    </>
  );
}

// ── Details: where, and the notice arithmetic ────────────────────────────────────────────────────

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-2 py-10 sm:grid-cols-[112px_minmax(0,1fr)] sm:gap-16">
      <dt className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary">{label}</dt>
      <dd className="min-w-0 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text">
        {children}
      </dd>
    </div>
  );
}

function MeetingDetails({
  meeting,
  calendar,
  onCancelled,
}: {
  meeting: OrgBoardMeetingOut;
  calendar: MeetingCalendarView;
  onCancelled: () => void;
}) {
  const { orgRef } = useBoardHost();
  const isCancelled = meeting.status === 'cancelled';
  // Where the last reschedule was made, when a calendar made it — read only once one has been.
  const events = useMeetingEvents(orgRef, meeting.id, (meeting.schedule_revision ?? 0) > 0);
  const madeIn = lastRescheduleProvider(events.data ?? []);
  const invitations = (
    <MeetingInvitationsLine
      meeting={meeting}
      view={calendar}
      onCancelled={onCancelled}
      row={(line) => <DetailRow label={calendarWords.rowLabel}>{line}</DetailRow>}
    />
  );
  return (
    <Card testId="meeting-summary">
      <dl className="flex flex-col divide-y divide-border-subtle px-20 py-4">
        {meeting.location || meeting.video_link ? (
          <DetailRow label="Where">
            {meeting.location}
            {meeting.location && meeting.video_link ? ' · ' : null}
            {meeting.video_link ? (
              <a
                href={meeting.video_link}
                target="_blank"
                rel="noreferrer"
                className="font-w600 text-brand-600 outline-none hover:text-brand-700 focus-visible:underline"
                data-testid="meeting-video-link"
              >
                Join online
              </a>
            ) : null}
          </DetailRow>
        ) : null}
        {isCancelled ? (
          <DetailRow label="Cancelled">
            <span data-testid="meeting-cancellation-reason">
              {meeting.cancellation_reason ?? 'No reason recorded'}
            </span>
          </DetailRow>
        ) : null}
        {invitations}
        <DetailRow label="Notice">
          {/* Inline, so the glyph follows the last word wherever the line wraps. */}
          <span data-testid="meeting-notice">
            {/* A committee's meeting counts against the committee's own period (CBG-YR6-MEET). */}
            {noticeFact(meeting.notice, meeting.meeting_type, meetingCommitteeName(meeting))}{' '}
            {meeting.notice?.statement ? (
              <InfoTip label="How the notice count is worked out" testId="meeting-notice-info">
                {/* The backend's sentence, verbatim and once: it already states a shortfall in days
                    ("4 days fewer than that period"), and a second, bold line would read as a
                    warning (the orchestrator's by-eye pass). */}
                <p data-testid="meeting-notice-statement">{meeting.notice.statement}</p>
              </InfoTip>
            ) : null}
          </span>
        </DetailRow>
        {(meeting.schedule_revision ?? 0) > 0 ? (
          <DetailRow label="Rescheduled">
            {/* A rescheduled meeting's revision is what makes a calendar entry update rather than
                duplicate; worth showing so an officer knows the file they hand out is current. */}
            {madeIn !== null
              ? calendarWords.rescheduledIn(meeting.schedule_revision ?? 0, madeIn)
              : `${meeting.schedule_revision} ${meeting.schedule_revision === 1 ? 'time' : 'times'}`}
          </DetailRow>
        ) : null}
      </dl>
    </Card>
  );
}
